import { HeaderPage } from "@/components/HeaderPage";
import { CardContent } from "@/components/CardContent";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Monitor,
  Smartphone,
  ExternalLink,
  Calendar,
  Link as LinkIcon,
  Pencil,
  Timer,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetBannerById } from "@/hooks/useBanners";
import { formatDate } from "@/lib/utils";
import { Loading } from "@/components/Loading";

export const ViewBanner = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: banner, isLoading } = useGetBannerById(id || "");

  if (isLoading) {
    return <Loading label="Carregando banner..." />;
  }

  if (!banner) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-gray-500">Banner não encontrado</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate("/banners")}
        >
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/banners")}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <HeaderPage
            title={banner.title}
            description="Visualize os detalhes do banner"
          />
        </div>
        <Button
          onClick={() => navigate(`/banners/edit/${id}`)}
          variant="outline"
          className="cursor-pointer"
        >
          <Pencil className="h-4 w-4 mr-2" />
          Editar Banner
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Imagem do Banner */}
        <div className="lg:col-span-2">
          <CardContent
            title="Preview do Banner"
            content={
              <div className="flex items-center justify-center bg-gray-50 rounded-lg p-4">
                <img
                  src={`${import.meta.env.VITE_BUCKET_URL}${banner.key}`}
                  alt={banner.title}
                  className="max-w-full max-h-96 object-contain rounded-lg shadow-sm"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                  }}
                />
              </div>
            }
          />
        </div>

        {/* Informações do Banner */}
        <div className="lg:col-span-1">
          <CardContent
            title="Informações"
            content={
              <div className="space-y-4">
                {/* Status */}
                <div>
                  <p className="text-sm text-gray-500 mb-1">Status</p>
                  <Badge variant={banner.isActive ? "success" : "destructive"}>
                    {banner.isActive ? "Ativo" : "Inativo"}
                  </Badge>
                </div>

                {/* Tipo */}
                <div>
                  <p className="text-sm text-gray-500 mb-1">Tipo</p>
                  <div className="flex items-center gap-2">
                    {banner.type === "DESKTOP" ? (
                      <Monitor className="h-4 w-4" />
                    ) : (
                      <Smartphone className="h-4 w-4" />
                    )}
                    <span className="font-medium">
                      {banner.type === "DESKTOP" ? "Desktop" : "Mobile"}
                    </span>
                  </div>
                </div>

                {/* Ordem */}
                <div>
                  <p className="text-sm text-gray-500 mb-1">Ordem de exibição</p>
                  <span className="font-medium">{banner.order}</span>
                </div>

                {/* Tempo de rolagem */}
                <div>
                  <p className="text-sm text-gray-500 mb-1">Tempo de rolagem</p>
                  <div className="flex items-center gap-2">
                    <Timer className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{banner.scrollTime ?? 5}s</span>
                  </div>
                </div>

                {/* Descrição */}
                {banner.description && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Descrição</p>
                    <p className="text-sm">{banner.description}</p>
                  </div>
                )}

                {/* Link */}
                {banner.linkUrl && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Link</p>
                    <a
                      href={banner.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <LinkIcon className="h-4 w-4" />
                      <span className="truncate">{banner.linkUrl}</span>
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    <p className="text-xs text-gray-400 mt-1">
                      Abre em:{" "}
                      {banner.linkTarget === "_blank" ? "Nova aba" : "Mesma aba"}
                    </p>
                  </div>
                )}

                {/* Data de criação */}
                <div>
                  <p className="text-sm text-gray-500 mb-1">Criado em</p>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{formatDate(banner.createdAt)}</span>
                  </div>
                </div>

                {/* Data de atualização */}
                {banner.updatedAt && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Última atualização</p>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{formatDate(banner.updatedAt)}</span>
                    </div>
                  </div>
                )}
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};
